import type { AssetManager } from "../../assets/asset-manager";
import type { CharacterDefinition } from "../../data/characters";
import {
  assignPartyMember,
  countPartyMembers,
  removePartyMember,
  type PartyState,
} from "./formation-state";

export interface FormationViewCallbacks {
  getPartyState: () => PartyState;
  updatePartyState: (party: PartyState) => void;
  onBack: () => void;
}

export class FormationView {
  readonly element: HTMLElement;
  private readonly assets: AssetManager;
  private readonly characters: CharacterDefinition[];
  private readonly callbacks: FormationViewCallbacks;
  private readonly memberCount: HTMLElement;
  private readonly rosterAvailable: HTMLElement;
  private readonly slotGrid: HTMLElement;
  private readonly rosterGrid: HTMLElement;
  private readonly selectedPanel: HTMLElement;
  private readonly selectedIcon: HTMLElement;
  private readonly selectedName: HTMLElement;
  private readonly selectedSubtitle: HTMLElement;
  private readonly selectedId: HTMLElement;
  private readonly selectedAssignment: HTMLElement;
  private readonly setButton: HTMLButtonElement;
  private readonly removeButton: HTMLButtonElement;
  private readonly slotButtons: HTMLButtonElement[] = [];
  private readonly rosterButtons: HTMLButtonElement[] = [];
  private selectedSlot = 0;
  private selectedCharacterId: string | null;

  constructor(
    root: HTMLElement,
    assets: AssetManager,
    characters: CharacterDefinition[],
    callbacks: FormationViewCallbacks,
  ) {
    this.assets = assets;
    this.characters = characters;
    this.callbacks = callbacks;
    this.selectedCharacterId = characters[0]?.id ?? null;
    this.element = document.createElement("main");
    this.element.className = "formation-screen scene-shell";
    this.element.innerHTML = `
      <div class="formation-atmosphere" aria-hidden="true"><span></span><i></i><i></i></div>
      <div class="formation-shell">
        <header class="formation-header">
          <button class="formation-back" type="button"><span aria-hidden="true">←</span> BACK</button>
          <div class="formation-title-block">
            <p class="formation-eyebrow">SOCIAL SYSTEM / PARTY 01</p>
            <h1>FORMATION</h1>
          </div>
          <div class="formation-member-count" aria-live="polite"><small>MEMBERS</small><strong class="member-count"></strong><em>PARTY 01 / SESSION</em></div>
        </header>

        <div class="formation-main">
          <section class="formation-party" aria-labelledby="party-heading">
            <div class="formation-section-heading"><div><p class="formation-eyebrow">01 / ACTIVE UNIT</p><h2 id="party-heading">PARTY</h2></div><span>SELECT A SLOT TO ASSIGN A CHARACTER</span></div>
            <div class="party-slot-grid" role="list" aria-label="Party slots"></div>
          </section>

          <div class="formation-lower">
            <section class="formation-roster" aria-labelledby="roster-heading">
              <div class="formation-section-heading"><div><p class="formation-eyebrow">02 / REGISTERED</p><h2 id="roster-heading">CHARACTER SELECT</h2></div><span class="roster-available"></span></div>
              <div class="roster-grid" role="list" aria-label="Registered characters"></div>
            </section>

            <aside class="formation-selected" aria-labelledby="selected-heading">
              <div class="selected-panel-rule"></div>
              <p class="formation-eyebrow">SELECTED CHARACTER</p>
              <div class="selected-character-row">
                <div class="selected-icon icon-frame"></div>
                <div><h2 id="selected-heading" class="selected-name"></h2><p class="selected-subtitle"></p></div>
              </div>
              <dl class="selected-meta"><div><dt>ASSIGNMENT</dt><dd class="selected-assignment"></dd></div><div><dt>CHARACTER ID</dt><dd class="selected-id"></dd></div></dl>
              <div class="formation-actions"><button class="formation-remove" type="button">REMOVE</button><button class="formation-set" type="button">SET MEMBER</button></div>
            </aside>
          </div>
        </div>

        <footer class="formation-footer"><span><i></i> SESSION PARTY STATE</span><span>FORMATION / 01</span><span>NO SERVER SYNC</span></footer>
      </div>
    `;
    root.replaceChildren(this.element);

    this.memberCount = this.element.querySelector<HTMLElement>(".member-count")!;
    this.rosterAvailable = this.element.querySelector<HTMLElement>(".roster-available")!;
    this.slotGrid = this.element.querySelector<HTMLElement>(".party-slot-grid")!;
    this.rosterGrid = this.element.querySelector<HTMLElement>(".roster-grid")!;
    this.selectedPanel = this.element.querySelector<HTMLElement>(".formation-selected")!;
    this.selectedIcon = this.element.querySelector<HTMLElement>(".selected-icon")!;
    this.selectedName = this.element.querySelector<HTMLElement>(".selected-name")!;
    this.selectedSubtitle = this.element.querySelector<HTMLElement>(".selected-subtitle")!;
    this.selectedId = this.element.querySelector<HTMLElement>(".selected-id")!;
    this.selectedAssignment = this.element.querySelector<HTMLElement>(".selected-assignment")!;
    this.setButton = this.element.querySelector<HTMLButtonElement>(".formation-set")!;
    this.removeButton = this.element.querySelector<HTMLButtonElement>(".formation-remove")!;

    this.element.querySelector<HTMLButtonElement>(".formation-back")!.addEventListener("click", callbacks.onBack);
    this.setButton.addEventListener("click", () => this.setSelectedMember());
    this.removeButton.addEventListener("click", () => this.removeSelectedMember());
    this.createSlotButtons();
    this.createRosterButtons();
    this.render();
  }

  private createSlotButtons(): void {
    for (let index = 0; index < 5; index += 1) {
      const button = document.createElement("button");
      button.className = "party-slot";
      button.type = "button";
      button.dataset.slotIndex = String(index);
      button.addEventListener("click", () => {
        this.selectedSlot = index;
        const characterId = this.callbacks.getPartyState()[index];
        if (characterId) this.selectedCharacterId = characterId;
        this.render();
      });
      this.slotButtons.push(button);
      this.slotGrid.append(button);
    }
  }

  private createRosterButtons(): void {
    this.characters.forEach((character) => {
      const button = document.createElement("button");
      button.className = "roster-character";
      button.type = "button";
      button.dataset.characterId = character.id;
      button.style.setProperty("--roster-accent", character.accent);
      button.setAttribute("aria-label", `Select ${character.name}`);
      button.addEventListener("click", () => {
        this.selectedCharacterId = character.id;
        this.render();
      });
      this.rosterButtons.push(button);
      this.rosterGrid.append(button);
    });
  }

  private render(): void {
    const party = this.callbacks.getPartyState();
    this.memberCount.textContent = `${countPartyMembers(party)} / 5`;
    this.rosterAvailable.textContent = `AVAILABLE ${String(this.characters.length).padStart(2, "0")}`;
    this.renderPartySlots(party);
    this.renderRoster(party);
    this.renderSelectedCharacter(party);
  }

  private renderPartySlots(party: PartyState): void {
    this.slotButtons.forEach((button, index) => {
      const characterId = party[index];
      const character = this.findCharacter(characterId);
      button.replaceChildren();
      button.classList.toggle("is-selected", index === this.selectedSlot);
      button.classList.toggle("is-empty", !character);
      button.setAttribute("aria-pressed", String(index === this.selectedSlot));
      button.setAttribute("aria-label", character ? `Party slot ${index + 1}, ${character.name}` : `Party slot ${index + 1}, empty`);
      if (character) {
        button.style.setProperty("--slot-accent", character.accent);
      } else {
        button.style.removeProperty("--slot-accent");
      }

      const header = document.createElement("span");
      header.className = "party-slot-header";
      header.innerHTML = `<b>${String(index + 1).padStart(2, "0")}</b><small>${index === this.selectedSlot ? "SELECTED" : "SLOT"}</small>`;
      button.append(header);

      const icon = document.createElement("span");
      icon.className = "party-slot-icon icon-frame";
      button.append(icon);
      if (character) {
        this.populateIcon(icon, character, false);
        const name = document.createElement("strong");
        name.textContent = character.name;
        const status = document.createElement("small");
        status.className = "party-slot-status";
        status.textContent = "MEMBER / IN PARTY";
        button.append(name, status);
      } else {
        const empty = document.createElement("span");
        empty.className = "party-slot-empty";
        empty.innerHTML = `<b>+</b><small>EMPTY SLOT</small><em>ADD MEMBER</em>`;
        button.append(empty);
      }
    });
  }

  private renderRoster(party: PartyState): void {
    this.rosterButtons.forEach((button) => {
      const selected = button.dataset.characterId === this.selectedCharacterId;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", String(selected));
      button.replaceChildren();
      const character = this.findCharacter(button.dataset.characterId ?? null);
      if (!character) return;
      const icon = document.createElement("span");
      icon.className = "roster-icon icon-frame";
      this.populateIcon(icon, character, false);
      const copy = document.createElement("span");
      copy.className = "roster-copy";
      const name = document.createElement("strong");
      name.textContent = character.name;
      const role = document.createElement("small");
      role.textContent = character.subtitle ?? character.tagline;
      const assignment = document.createElement("em");
      assignment.className = "roster-assignment";
      const assignedSlot = this.assignedSlotFor(character.id, party);
      assignment.textContent = assignedSlot === undefined ? "AVAILABLE" : `IN PARTY / ${String(assignedSlot + 1).padStart(2, "0")}`;
      copy.append(name, role);
      copy.append(assignment);
      button.append(icon, copy);
    });
  }

  private renderSelectedCharacter(party: PartyState): void {
    const character = this.findCharacter(this.selectedCharacterId);
    this.setButton.disabled = !character;
    this.removeButton.disabled = party[this.selectedSlot] === null;
    this.selectedPanel.classList.toggle("has-character", Boolean(character));
    this.selectedPanel.style.setProperty("--selected-accent", character?.accent ?? "#ff8ebc");
    this.selectedIcon.replaceChildren();
    if (!character) {
      this.selectedName.textContent = "NO CHARACTER";
      this.selectedSubtitle.textContent = "Select a registered character.";
      this.selectedAssignment.textContent = "—";
      this.selectedId.textContent = "—";
      return;
    }
    this.populateIcon(this.selectedIcon, character, true);
    this.selectedName.textContent = character.name;
    this.selectedSubtitle.textContent = character.subtitle ?? character.tagline;
    const assignedSlot = this.assignedSlotFor(character.id, party);
    this.selectedAssignment.textContent = assignedSlot === undefined ? "UNASSIGNED" : `SLOT ${String(assignedSlot + 1).padStart(2, "0")}`;
    this.selectedId.textContent = character.id;
  }

  private setSelectedMember(): void {
    if (!this.selectedCharacterId) return;
    const character = this.findCharacter(this.selectedCharacterId);
    if (!character) return;
    this.callbacks.updatePartyState(assignPartyMember(this.callbacks.getPartyState(), this.selectedSlot, character.id));
    this.render();
  }

  private removeSelectedMember(): void {
    if (this.callbacks.getPartyState()[this.selectedSlot] === null) return;
    this.callbacks.updatePartyState(removePartyMember(this.callbacks.getPartyState(), this.selectedSlot));
    this.render();
  }

  private findCharacter(characterId: string | null): CharacterDefinition | undefined {
    return characterId ? this.characters.find((character) => character.id === characterId) : undefined;
  }

  private assignedSlotFor(characterId: string, party: PartyState): number | undefined {
    const slot = party.indexOf(characterId);
    return slot === -1 ? undefined : slot;
  }

  private populateIcon(container: HTMLElement, character: CharacterDefinition, selected: boolean): void {
    container.dataset.characterId = character.id;
    const fallback = document.createElement("span");
    fallback.className = "icon-fallback";
    fallback.textContent = character.name.slice(0, 1).toUpperCase();
    const image = document.createElement("img");
    image.alt = selected ? `${character.name} icon` : "";
    image.hidden = true;
    container.append(fallback, image);
    void this.assets.loadImage(character.icon).then((loaded) => {
      if (!container.isConnected || container.dataset.characterId !== character.id) return;
      image.src = loaded.src;
      image.hidden = false;
      fallback.hidden = true;
    }).catch(() => {
      // The CSS fallback remains visible when an icon is unavailable.
    });
  }

  destroy(): void {
    this.element.remove();
  }
}
